import { Component, computed, inject } from "@angular/core";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { BalancePoint } from "../../../models/types/balanceGroupingTypes";
import { CalculationPastStoreService } from "../../../services/store/calculation-past/calculation-past.store.service";
import { Observable } from "rxjs";
// Global, unencapsulated stylesheet: injected via this side-effect import, not Angular's
// styleUrls/styleUrl, so it isn't scoped to this component.
import "./balance-chart.component.scss";

import { Scales } from "@bitwarden/web-vault/app/components/charts/shared/scales";
import { BaseBalanceChartComponent } from "@bitwarden/web-vault/app/components/charts/shared/base-balance-chart.component";
import {
  InteractionState,
  InteractionStateKey,
} from "@bitwarden/web-vault/app/components/charts/shared/interaction-state";

@Component({
  selector: "app-balance-chart",
  templateUrl: "./balance-chart.component.html",
})
export class BalanceChartComponent extends BaseBalanceChartComponent {
  // balance chart specific properties
  protected graphContainerSelectString = "div#balanceLineChart";
  protected graphContainerID = "balanceGraphContainer";
  protected graphGroupID = "balanceLineChartGroup";

  // subscribe to signal stores
  private calculationStoreService = inject(CalculationPastStoreService);
  protected groupControl = this.calculationStoreService.groupings.selected();
  private balancePoints$: Observable<Map<string, BalancePoint>> = toObservable(
    this.calculationStoreService.balanceByTime.balancePoints,
  );

  // Graph Data
  private balancesByTime: Map<string, number>;
  private insByTime: Map<string, number>;
  private outsByTime: Map<string, number>;

  tickValues = computed(() => {
    const balances = this.calculationStoreService.balanceByTime.balancePoints();
    if (balances) {
      return [...balances.keys()].map((time) => new Date(Number(time) * 1000));
    }
  });

  constructor() {
    super();
    this.balancePoints$.pipe(takeUntilDestroyed()).subscribe(this.updateGraph.bind(this));
  }

  /**
   * Triggers when the balance numbers have updated on the store
   *
   */
  updateGraph() {
    super.updateGraph();
    // get the granularity from the groupings signal
    this.updateGranularity();

    // Create a local copy of the balance by time Map
    const balancePoints = this.calculationStoreService.balanceByTime.balancePoints();

    // The store's initial value is an empty Map, which is truthy, so a bare null check would let
    // that first run fall through to the full draw on no data (#4977). After mount the store only
    // holds data (the plotted dates always include an opening point) or null (clear()), so the
    // size check matters for the initial value only.
    if (!balancePoints || balancePoints.size === 0) {
      // #4602: also clear the balance line and point circles left over from the previous
      // render - the marker-line removal below never touched them, so a null path (e.g.
      // purge/reload) left a stale line + dots on screen with no boxes.
      this.dataLine?.removeMarkerLine();
      this.dataLine?.removeBalanceLines();
      this.dataPoints?.removeAllPoints();
      // removeMarkerLine() only detaches the #markerLine DOM node - it doesn't touch this cached
      // selection, which stays truthy forever afterward. Left alone, initMarker()'s own
      // `!this.markerLine` guard would keep treating the marker as already created and never
      // recreate it once data repopulates (see the initGraphElements() latch below).
      this.markerLine = null;
      // clearData() (via super.updateGraph() above) only clears the hover boxes - the
      // pinned/default inBoxes/outBoxes from the previous render would otherwise stick
      // around on screen after the data disappears (see #4157).
      this.clearPinnedBoxes();
      // Reset so a pointer already over the chart (mousemove/mouseleave, still wired from
      // ngOnInit) doesn't redraw the boxes just removed above from stale position data.
      this.balancePointData = [];
      return;
    }

    // Create a balance by time Map of the plotDates in balancePoints
    this.balancesByTime = new Map();
    this.insByTime = new Map();
    this.outsByTime = new Map();
    const openingPoints: number[] = [];
    let index = 0;
    balancePoints.forEach((balancePoint) => {
      this.balancesByTime.set(balancePoint.plotDate.toString(), balancePoint.balance);
      this.insByTime.set(balancePoint.plotDate.toString(), balancePoint.ins);
      this.outsByTime.set(balancePoint.plotDate.toString(), balancePoint.outs);

      if (balancePoint.plotType === "opening") {
        openingPoints.push(index);
      }
      index++;
    });

    const startDate = this.calculationStoreService.period.selected()?.startDate;
    const endDate = this.calculationStoreService.period.selected()?.endDate;

    if (!this.graphElementsInitialized) {
      this.initGraphElements();
    } else if (!this.markerLine) {
      // Repopulating after a purge -> clearStores() -> null cycle: graphElementsInitialized is a
      // one-shot latch set permanently true on the first draw, so initGraphElements() above never
      // runs again and would otherwise leave the marker line removed by the null-path cleanup
      // above gone for the lifetime of this component instance. Re-invoking initMarker() directly
      // recreates it now that markerLine has been nulled out (its guard needs markerLine falsy).
      this.initMarker();
    }

    this.drawScalesFromData(
      startDate,
      endDate,
      this.balancesByTime,
      this.insByTime,
      this.outsByTime,
    );
    this.drawLines(this.balancesByTime);
    this.drawPoints(this.balancesByTime, openingPoints);
    this.drawInsAndOuts(this.balancesByTime, this.insByTime, this.outsByTime);

    if (this.balancePointData.length > 0) {
      this.setHudData(this.balancePointData.length - 1);

      const [lastPointX, lastPointY] = this.balancePointData[this.balancePointData.length - 1];
      this.dataLine.setMarkerLinePinned(false, true);
      this.dataLine.moveMarkerLine(lastPointX, lastPointY);
      this.dataPoints.setDefaultPoint("balancePoints", this.balancePointData.length - 1);
    }
  }

  /**
   * Triggered when there is mouse movement on the graph. Overrides the base class method to update the in and out boxes.
   * @protected
   */
  updateInOutBoxes(index?: number, type: InteractionStateKey = InteractionState.Pinned) {
    const inClass = type === InteractionState.Pinned ? "inBoxes" : "inBoxes-hover";
    const outClass = type === InteractionState.Pinned ? "outBoxes" : "outBoxes-hover";
    this.drawInsAndOuts(
      this.balancesByTime,
      this.insByTime,
      this.outsByTime,
      index,
      inClass,
      outClass,
    );
  }

  adjustStartOfDay(balances: Map<string, number>): Map<string, number> {
    const startOfDayTimes: Record<string, number> = {};
    const secondsToSubtract = 86400 - 1;

    balances.forEach((amount, time) => {
      const startOfDay = Number(time) - secondsToSubtract;
      startOfDayTimes[startOfDay] = amount;
    });
    return new Map(Object.entries(startOfDayTimes));
  }

  // <editor-fold desc="Graph Drawing Functions">

  private drawScalesFromData(
    startDate: Date,
    endDate: Date,
    balancesByTime: Map<string, number>,
    insByTime: Map<string, number>,
    outsByTime: Map<string, number>,
  ) {
    const [minYAxisValue, maxYAxisValue] = Scales.calculateMaxMinYAxisValues(
      balancesByTime,
      insByTime,
      outsByTime,
    );

    this.drawScales(startDate, endDate, minYAxisValue, maxYAxisValue, this.tickValues());
  }

  private drawPoints(balancesByTime: Map<string, number>, openingPoints: number[]) {
    this.balancePointData = this.dataPoints.generatePointData(balancesByTime);
    this.balancePointDates = this.dataPoints.generatePointDates(balancesByTime);

    this.dataPoints.updatePoints("balancePoints", this.balancePointData);
    this.dataPoints.setOpeningPoints("balancePoints", openingPoints);
  }

  private drawLines(balancesByTime: Map<string, number>) {
    this.dataLine.updateBalanceLineGraph(balancesByTime);
  }

  selectPoint(i: number) {
    this.dataPoints.setSelectedPoint("balancePoints", i);
  }

  updateGranularity() {
    this.granularity = this.calculationStoreService.groupings.selected()?.granularity;
  }

  // </editor-fold desc="Graph Drawing Functions">
}
